import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { keyframes } from '@mui/material/styles';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

/**
 * Clock-style loader using theme primary. Use for full-page/section loading or inline in buttons.
 *
 * @param {number} [size=48] — width/height in px
 * @param {string} [message] — optional text below the clock
 * @param {string} [height] — min height when message is shown (default auto)
 * @param {boolean} [inline] — no flex wrapper; for buttons (default false)
 */
const ClockLoader = ({ size = 48, message, height, inline = false }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const clock = (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 40 40"
      sx={{ display: 'block', flexShrink: 0 }}
      aria-hidden
    >
      <circle
        cx="20"
        cy="20"
        r="17"
        fill="none"
        stroke={primary}
        strokeWidth="2"
        opacity={0.2}
      />
      <Box
        component="g"
        sx={{
          transformBox: 'fill-box',
          transformOrigin: 'center',
          animation: `${spin} 1.1s linear infinite`,
        }}
      >
        <line
          x1="20"
          y1="20"
          x2="20"
          y2="9"
          stroke={primary}
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      </Box>
      <Box
        component="g"
        sx={{
          transformBox: 'fill-box',
          transformOrigin: 'center',
          animation: `${spin} 4s linear infinite`,
        }}
      >
        <line
          x1="20"
          y1="20"
          x2="28"
          y2="20"
          stroke={primary}
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity={0.85}
        />
      </Box>
    </Box>
  );

  if (inline) {
    return clock;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight={height ?? (message ? 120 : undefined)}
      width="100%"
      py={message ? 2 : 0}
    >
      {clock}
      {message ? (
        <Typography mt={2} variant="body2" color="text.secondary" textAlign="center">
          {message}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ClockLoader;
